import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, { Children, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { Button } from "semantic-ui-react";
import "./Carousel.scss";
import classNames from "classnames";

type PropType = {
  options?: EmblaOptionsType;
  children?: React.ReactNode;
};

const Carousel: React.FC<PropType> = (props) => {
  const { children, options } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);
  const count = Children.count(children);
  if (count === 0) return null;

  return (
    <section className='carousel tw-relative'>
      <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
      <div className='carousel-viewport' ref={emblaRef}>
        <div className='carousel-container'>{children}</div>
      </div>
      <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
    </section>
  );
};
export default Carousel;

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const usePrevNextButtons = (emblaApi: EmblaCarouselType | undefined): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

function PrevButton({ children, onClick, disabled }: PropsWithChildren<{ onClick: () => void; disabled: boolean }>) {
  return (
    <div
      className={classNames(
        "tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-z-50 tw-flex tw-h-full tw-w-36 tw-items-center tw-justify-start tw-bg-gradient-to-l tw-from-transparent tw-to-white tw-transition-opacity tw-duration-100 tw-ease-out",
        {
          "tw-opacity-0": disabled,
          "tw-opacity-100": !disabled,
        },
      )}
    >
      <Button
        secondary
        className='tw-pointer-events-auto tw-m-0 tw-h-10 tw-w-10 tw-p-0 tw-shadow-md'
        onClick={onClick}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faChevronRight} rotation={180} />
        {children}
      </Button>
    </div>
  );
}

function NextButton({ children, onClick, disabled }: PropsWithChildren<{ onClick: () => void; disabled: boolean }>) {
  return (
    <div
      className={classNames(
        "tw-pointer-events-none tw-absolute tw-right-0 tw-top-0 tw-flex tw-h-full tw-w-36 tw-items-center tw-justify-end tw-bg-gradient-to-r tw-from-transparent tw-to-white tw-transition-opacity tw-duration-100 tw-ease-out",
        {
          "tw-opacity-0": disabled,
          "tw-opacity-100": !disabled,
        },
      )}
    >
      <Button
        secondary
        className='tw-pointer-events-auto tw-m-0 tw-h-10 tw-w-10 tw-p-0 tw-shadow-md'
        onClick={onClick}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faChevronRight} />
        {children}
      </Button>
    </div>
  );
}
